// billing.js — Account → Billing panel + the side-effects that
// surround it: Stripe checkout redirect, Stripe portal redirect, plan
// changes (upgrade / downgrade / cancel / resume), Buy-more-seats
// inline link, ?billing=success / ?billing=portal-return URL return
// handler, and the ?tier=<tier> auto-checkout entry point.
//
// Exports:
//   renderBillingStatus — called by pages/settings.js mount
//   startCheckout       — called by analytics.js upgrade button
//
// Re-exposed on AC_LEGACY by bootstrap.js so existing consumers
// (pages/settings.js + analytics.js + the inline upgrade nudge on the
// My Machines pager) keep working without touching their imports.
//
// Also exposed on window:
//   window.renderBillingStatus — for the inline onclick string on the
//                                My Machines "Upgrade for more slots"
//                                nudge (rendered as HTML by another
//                                renderer that calls renderBillingStatus
//                                via the DOM, not via import).
//   window._billing            — authoritative billing snapshot used
//                                by click handlers (seats math etc.)
//   window._pickerOpen         — plan picker open/closed bit so the
//                                5s polling tick doesn't slam the
//                                picker shut on the user
//   window._applyTierIntent    — boot orchestrator calls this once
//                                billing status is known so that
//                                ?tier=startup users land on Stripe
//                                checkout automatically
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2h).
import { $ } from "./dom.js";
import { showToast } from "./ui.js";
import { api, apiWithMessage } from "./api.js";
import { state } from "./state.js";
import { teamState } from "./page-state.js";
import { navigate } from "./router.js";
import { renderTrialBanner, writeAuthHintCookie } from "./trial-banner.js";
import {
  showCancellationSurvey,
  showBillingChangeConfirm,
} from "./billing-modals.js";
import { renderTeamPage } from "./pages/team.js";
import { peekCheckoutResume, clearCheckoutResume } from "./checkout-resume.js";

// track is a window global installed by dashboard-init.js. The legacy
// bootstrap code called bare `track(...)` which silently fell through
// to the no-op stub if telemetry was disabled.
function track(event, props) {
  if (typeof window.track === "function") window.track(event, props);
}

export async function renderBillingStatus() {
  const badge = $('billing-tier-badge');
  const planDesc = $('billing-plan-desc');
  const membersDesc = $('billing-members-desc');
  const manageDesc = $('billing-manage-desc');
  if (!badge) return;
  try {
    const billing = await api('/v1/billing/status');
    const teamInfo = await api('/v1/teams/info').catch(() => ({ your_role: 'member' }));
    const isAdmin = teamInfo.your_role === 'admin';
    // Cache for click handlers (startCheckout / changePlan / button routing).
    // Any code that needs the authoritative seat count, trial state, or tier
    // should read window._billing — NOT scrape the badge or count laptops.
    window._billing = billing;
    renderTrialBanner(billing);
    writeAuthHintCookie(billing.tier);

    // Badge. Tier IDs stay as 'solo'/'startup'/'enterprise' in the DB;
    // the public-facing names are Free/Team/Enterprise (2026-05-15).
    const tierLabels = { solo: 'Free', startup: 'Team', enterprise: 'Enterprise' };
    const tierColors = {
      solo:       { bg: 'var(--bg-elevated)', fg: 'var(--text-secondary)' },
      startup:    { bg: 'var(--accent-bg-strong)', fg: 'var(--accent)' },
      enterprise: { bg: 'var(--info-bg)', fg: 'var(--info)' },
    };
    const colors = tierColors[billing.tier] || tierColors.solo;
    badge.textContent = tierLabels[billing.tier] || billing.tier;
    badge.style.background = colors.bg;
    badge.style.color = colors.fg;

    // Comp (complimentary) accounts: a durable owner-granted free account
    // (no Stripe subscription). Show a distinct "Comped" badge so it's clear
    // this account is intentionally free rather than a lapsed/paid state, and
    // suppress the price/renewal line. Everything else (upgrade buttons hidden
    // for enterprise/active) already renders correctly off tier/status.
    if (billing.comp) {
      badge.textContent = 'Comped';
      badge.style.background = 'var(--info-bg)';
      badge.style.color = 'var(--info)';
    }

    // Plan description — short and factual. Details live in the picker.
    const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'unknown';
    const tierPrice = billing.tier === 'startup' ? '$8/user/mo' : billing.tier === 'enterprise' ? '$25/user/mo' : 'Free';
    const tierName = billing.tier === 'startup' ? 'Team' : billing.tier === 'enterprise' ? 'Enterprise' : 'Free';

    if (billing.comp) {
      planDesc.innerHTML = '<strong>Complimentary</strong> &mdash; no charge. Full access with no subscription.';
    } else if (billing.tier === 'solo') {
      planDesc.innerHTML = 'Free forever. Single-machine coordination only. Upgrade for cross-machine team coordination.';
    } else {
      let line = '<strong>' + tierName + '</strong> &mdash; ' + tierPrice + '.';
      if (billing.is_trialing && billing.trial_end) {
        const days = Math.max(0, Math.ceil((new Date(billing.trial_end) - new Date()) / 86400000));
        line += ' <span style="color: var(--accent); font-weight: 600;">Free trial &mdash; ' + days + ' day' + (days === 1 ? '' : 's') + ' left</span> (ends ' + fmtDate(billing.trial_end) + ').';
      } else if (billing.cancel_at_period_end) {
        line += ' <span style="color: var(--warning);">Cancels on ' + fmtDate(billing.current_period_end) + '.</span>';
      } else if (billing.status === 'past_due') {
        line += ' <span style="color: var(--danger); font-weight: 600;">Payment past due.</span> Update your payment method to continue service.';
      } else if (billing.status === 'canceled') {
        line += ' <span style="color: var(--text-tertiary);">Canceled.</span>';
      } else {
        line += ' <span style="color: var(--text-tertiary);">Renews ' + fmtDate(billing.current_period_end) + '.</span>';
      }
      planDesc.innerHTML = line;
    }

    // Members count vs paid seats. The coordinator now exposes
    // `max_members = subscriptions.quantity` for paid tiers (was
    // tier-based 1M cap before v0.17.24), so this line shows
    // "X / Y paid seats" with real numbers — and a "Buy more
    // seats" button when the team is at or near capacity.
    const max = billing.max_members;
    const isPaid = billing.tier === 'startup' || billing.tier === 'enterprise';
    let html;
    if (billing.comp) {
      // Comp accounts have no Stripe quantity — max_members is a sentinel
      // (Number.MAX_SAFE_INTEGER). Render "unlimited" instead of the raw
      // 16-digit number, and never show the buy-seats link (there is no
      // Stripe subscription to add seats to).
      html = '<span style="color: var(--text-secondary);">' + billing.member_count +
        ' member' + (billing.member_count === 1 ? '' : 's') + ' &mdash; unlimited seats (complimentary).</span>';
    } else {
      const overLimit = billing.member_count > max;
      const atOrNearLimit = isPaid && max > 0 && billing.member_count >= max - 1;
      const memberColor = overLimit ? 'var(--danger)' : (atOrNearLimit ? 'var(--warning)' : 'var(--text-secondary)');
      const seatsLabel = isPaid ? 'paid seats' : 'included in your plan';
      html = '<span style="color: ' + memberColor + '; font-weight: ' + (overLimit ? '600' : 'normal') + ';">' +
        billing.member_count + ' / ' + max + ' ' + seatsLabel + '.</span>';
      if (overLimit) {
        html += ' <strong style="color: var(--danger);">Over the limit &mdash; remove a member or buy more seats.</strong>';
      } else if (atOrNearLimit) {
        html += ' <span style="color: var(--warning);">' + (max - billing.member_count) + ' seat' + ((max - billing.member_count) === 1 ? '' : 's') + ' left.</span>';
      }
      // "Buy more seats" inline button for paid tiers, admin only.
      // Opens Stripe portal in update-quantity mode. Always available
      // (not just at-limit) so admins can pre-buy seats before a
      // hiring spike.
      if (isPaid && state.teamInfo && state.teamInfo.your_role === 'admin') {
        html += ' <a href="#" id="billing-buy-more-seats" style="margin-left: 8px; color: var(--accent); font-weight: 600; font-size: 12px;">Buy more seats &rarr;</a>';
      }
    }
    membersDesc.innerHTML = html;

    // Machine count vs the plan's machine cap. Best-effort — a failure here must
    // not break the billing page. Uses the coordinator's corrected signals:
    //   running_count = daemons alive (any check-in, push OR pull, last 5min)
    //   active_count  = machines holding a concurrency SLOT (the cap unit)
    //   active_cap    = the tier's maxActiveMachinesPerUser (1/2/5)
    // We show the slot/cap as the billing-relevant number (that's what's
    // capped), and the honest "running" count alongside it.
    renderMachineCount(membersDesc, isPaid).catch(() => { /* best-effort */ });

    // Wire the link after inserting the HTML.
    const buySeatsLink = document.getElementById('billing-buy-more-seats');
    if (buySeatsLink) {
      buySeatsLink.onclick = async (e) => {
        e.preventDefault();
        try {
          buySeatsLink.textContent = 'Opening Stripe...';
          const portal = await api('/v1/billing/portal', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ flow: 'update_quantity' }),
          });
          if (portal.url) {
            location.href = portal.url;
          } else {
            showToast('error', 'Could not open Stripe portal.');
            buySeatsLink.textContent = 'Buy more seats →';
          }
        } catch (err) {
          showToast('error', 'Buy seats failed: ' + (err.message || 'unknown'));
          buySeatsLink.textContent = 'Buy more seats →';
        }
      };
    }

    // Picker + action buttons
    const picker = $('billing-plan-picker');
    const portalRow = $('billing-portal-row');
    const cancelRow = $('billing-cancel-row');
    const cancelSubRow = $('billing-cancel-sub-row');
    const togglePicker = $('billing-toggle-picker');
    const btnStartup = $('billing-upgrade-startup');
    const btnEnterprise = $('billing-upgrade-enterprise');
    const btnSolo = $('billing-current-solo');
    const heading = $('billing-picker-heading');
    const sub = $('billing-picker-sub');
    const cancelDesc = $('billing-cancel-desc');

    // Default: all action rows hidden. The picker's open/closed state is
    // preserved across re-renders (window._pickerOpen) so periodic polling
    // doesn't slam it shut while the user is reading it.
    if (portalRow) portalRow.style.display = 'none';
    if (cancelRow) cancelRow.style.display = 'none';
    if (cancelSubRow) cancelSubRow.style.display = 'none';
    if (togglePicker) togglePicker.style.display = 'none';
    if (picker) picker.style.display = window._pickerOpen ? '' : 'none';

    // Seat-quantity input. Visible to admins on Solo tier (Stripe
    // Checkout flow). Hidden on paid tiers because tier-changes go
    // through change-plan which uses portal proration; Stripe handles
    // seat changes there. Default value is the current member count.
    const seatRow = document.getElementById('billing-seat-row');
    const seatInput = document.getElementById('billing-seat-qty');
    const seatHelp = document.getElementById('billing-seat-help');
    const showSeatRow = isAdmin && billing.tier === 'solo';
    if (seatRow) seatRow.style.display = showSeatRow ? 'flex' : 'none';
    if (seatInput && showSeatRow) {
      const memberCount = Math.max(1, billing.member_count || 1);
      // Only set the default the first time the row appears so we
      // don't trample a value the user just typed during a re-render.
      if (!seatInput.dataset.userTouched) {
        seatInput.value = String(memberCount);
        seatInput.min = String(memberCount);
      }
      // Bind the userTouched listener once. Pre-fix this attached a
      // fresh `{once: true}` listener on every render, so a user
      // who opened+closed the billing card N times before typing
      // accumulated N pending listeners (all idempotent, all firing
      // on first keypress, all writing the same flag). Harmless
      // but wasteful.
      if (!seatInput.dataset.touchBound) {
        seatInput.addEventListener('input', () => { seatInput.dataset.userTouched = '1'; }, { once: true });
        seatInput.dataset.touchBound = '1';
      }
      if (seatHelp) {
        seatHelp.textContent = memberCount > 1
          ? `Defaults to your team size (${memberCount}). Set higher to pre-purchase licenses.`
          : 'Defaults to 1. Set higher to buy a bucket of licenses up front.';
      }

      // Contact-sales hint: shown when seat count is at/above the
      // self-serve "this should probably be a real conversation"
      // threshold. Listens on the seat input so toggling
      // 24 ↔ 25 reveals/hides the panel live without a re-render.
      const SALES_THRESHOLD = 25;
      const contactBox = document.getElementById('billing-contact-sales');
      const updateContact = () => {
        if (!contactBox) return;
        const n = parseInt(seatInput.value, 10);
        contactBox.style.display = (Number.isFinite(n) && n >= SALES_THRESHOLD) ? '' : 'none';
      };
      // Initial sync + live update.
      updateContact();
      if (!seatInput.dataset.contactBound) {
        seatInput.addEventListener('input', updateContact);
        seatInput.dataset.contactBound = '1';
      }
    } else {
      // Solo→paid not applicable (existing paid tier or non-admin
      // viewer). Make sure the contact-sales box doesn't linger
      // visible from a prior render where the seat row was shown.
      const contactBox = document.getElementById('billing-contact-sales');
      if (contactBox) contactBox.style.display = 'none';
    }

    if (!isAdmin) {
      // Non-admins: show read-only plan info with a "View plans" button
      if (togglePicker) {
        togglePicker.style.display = '';
        togglePicker.textContent = 'View plans';
      }
      btnStartup.disabled = true; btnStartup.textContent = 'Ask admin';
      btnEnterprise.disabled = true; btnEnterprise.textContent = 'Ask admin';
      if (btnSolo) btnSolo.textContent = billing.tier === 'solo' ? 'Current plan' : 'Solo';
      heading.textContent = 'Your team plan';
      sub.textContent = 'Only admins can change the plan. Ask your team admin to upgrade or downgrade.';
    } else if (billing.comp) {
      // Comp account: no Stripe subscription exists, so the Stripe portal,
      // cancel, downgrade, and upgrade controls don't apply. Show a read-only
      // "complimentary" state rather than paid-tier management buttons (which
      // would 4xx against Stripe). An owner can still revoke via the admin API.
      if (togglePicker) togglePicker.style.display = 'none';
      if (portalRow) portalRow.style.display = 'none';
      if (cancelRow) cancelRow.style.display = 'none';
      if (cancelSubRow) cancelSubRow.style.display = 'none';
      [btnSolo, btnStartup, btnEnterprise].forEach((b) => { if (b) b.style.display = 'none'; });
      heading.textContent = 'Complimentary account';
      sub.textContent = 'This team has full access at no charge. There is no subscription to manage.';
    } else if (billing.tier === 'solo') {
      // Solo: show "Choose a plan" toggle
      if (togglePicker) {
        togglePicker.style.display = '';
        togglePicker.textContent = 'Choose a plan';
      }
      btnStartup.style.display = ''; btnStartup.disabled = false; btnStartup.textContent = 'Upgrade to Startup';
      btnEnterprise.style.display = ''; btnEnterprise.disabled = false; btnEnterprise.textContent = 'Upgrade to Enterprise';
      btnSolo.textContent = 'Current plan'; btnSolo.disabled = true;
      heading.textContent = 'Choose a plan';
      // Trial eligibility comes from the server (`trial_eligible`, computed by
      // the same function checkout uses), NOT guessed client-side. The old
      // `!billing.stripe_customer_id` heuristic was per-tenant, so every newly
      // created team re-pitched a "14-day free trial" to users who had already
      // used theirs on another team.
      const trialEligible = billing.trial_eligible === true;
      sub.textContent = trialEligible
        ? 'All paid plans include a 14-day free trial. No credit card required to start. Cancel anytime.'
        : 'Tier changes apply immediately with prorated billing. Cancel anytime.';
    } else {
      // Paid tier: show Manage plan, Stripe portal, and Cancel options
      if (togglePicker) {
        togglePicker.style.display = '';
        togglePicker.textContent = 'Manage plan';
      }
      if (portalRow) portalRow.style.display = '';

      // Cancel-at-period-end banner + resume option
      if (billing.cancel_at_period_end) {
        if (cancelRow) cancelRow.style.display = '';
        if (cancelDesc) cancelDesc.innerHTML = 'Your subscription cancels on <strong>' + fmtDate(billing.current_period_end) + '</strong>. You’ll keep access until then. Resume to continue renewing.';
      } else if (billing.status === 'active' || billing.status === 'trialing') {
        // Allow cancellation
        if (cancelSubRow) cancelSubRow.style.display = '';
      }

      btnSolo.textContent = 'Downgrade to Solo'; btnSolo.disabled = false;
      if (billing.tier === 'startup') {
        btnStartup.textContent = 'Current plan'; btnStartup.disabled = true;
        btnEnterprise.textContent = 'Upgrade to Enterprise'; btnEnterprise.disabled = false;
        heading.textContent = 'Change your plan';
      } else {
        btnStartup.textContent = 'Downgrade to Startup'; btnStartup.disabled = false;
        btnEnterprise.textContent = 'Current plan'; btnEnterprise.disabled = true;
        heading.textContent = 'Change your plan';
      }
      sub.innerHTML = 'Plan changes take effect immediately with prorated billing. Downgrading to Solo cancels your subscription at the end of the current period.';
    }
  } catch (e) {
    badge.textContent = 'Error';
    planDesc.textContent = 'Could not load billing status: ' + (e.message || 'unknown');
  }
  // Consumption meter — rendered UNCONDITIONALLY (outside the try) so it always
  // paints even if /v1/billing/status failed above. Its own fetch + error state
  // means a meter hiccup never breaks the rest of the card, and a status-fetch
  // failure never leaves the usage row blank.
  renderConsumptionMeter().catch((err) => console.warn('consumption meter failed:', err));
}

// Client-side countdown ticker handle so re-renders don't stack intervals.
let _consumptionCountdownTimer = null;

// renderConsumptionMeter (PR3) — Claude-style rolling-24h usage meter:
// a progress bar + "1,480 / 2,000 events used — resets in 1h 10m". The
// thresholds (limit, resets_at) come from /v1/billing/consumption, which shares
// read24hMeter + the tier caps with the server-side enforcement gate, so the UI
// and the gate can never disagree. At >=80% (or over the limit) it nudges the
// upgrade prompt once per reset window.
// fmtCountdown — "4h 12m" / "12m" / "30s" from an ISO/space-format UTC stamp.
//
// Accepts BOTH shapes this stamp arrives in:
//   "2026-08-29 04:00:00"  — D1 datetime(), space-separated, no zone
//   "2026-08-29T04:00:00Z" — RFC3339, already zoned (what /v1/billing/
//                            consumption actually returns today)
//
// The old code unconditionally appended 'Z', which turned the second shape
// into "...04:00:00ZZ" → new Date() → NaN → the panel rendered a literal
// "Resets in NaNh NaNm" (2026-08-28). Only add the zone when there isn't one.
// Returns '' on an unparseable stamp so the caller drops the clause entirely
// rather than printing NaN.
function fmtUsageCountdown(iso) {
  if (!iso) return '';
  const norm = String(iso).replace(' ', 'T');
  // Already zoned? Trailing 'Z', or a +hh:mm / -hh:mm offset.
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(norm);
  const ms = new Date(zoned ? norm : norm + 'Z').getTime();
  if (!isFinite(ms)) return '';
  const secs = Math.max(0, Math.floor((ms - Date.now()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return h + 'h ' + m + 'm';
  if (m > 0) return m + 'm';
  return secs + 's';
}

// Renders the consumption meter into #billing-usage-desc. Always paints a real
// bar — tier label, used/allocation, a reset countdown, and a graceful state if
// the fetch fails — so the user never sees a bare placeholder. Reuses the same
// limit/resets_at the server enforcement gate uses, so UI and gate can't differ.
function paintUsageMeter(el, c, tierLabel) {
  const fmt = (n) => (n || 0).toLocaleString();
  const enterprise = c.tier === 'enterprise' || c.hard_block === false;
  const pct = Math.max(0, Math.min(100, c.pct || 0));
  const color = c.over_limit || pct >= 95 ? 'var(--danger)'
    : pct >= 75 ? 'var(--warning)'
    : 'var(--accent)';
  const resetsIn = fmtUsageCountdown(c.resets_at);

  // Header: "Free plan — 1,264 / 2,000 events today" (or "effectively unlimited"
  // for enterprise, which has a finite-but-huge soft ceiling).
  const allocation = enterprise
    ? fmt(c.used_24h) + ' events today &middot; <span style="color: var(--text-tertiary);">effectively unlimited</span>'
    : fmt(c.used_24h) + ' / ' + fmt(c.limit) + ' events today';
  const header =
    '<div style="display:flex; align-items:baseline; justify-content:space-between; gap:8px; margin-bottom:8px;">' +
      '<span style="color: var(--text);"><strong>' + tierLabel + ' plan</strong> &mdash; ' + allocation + '</span>' +
      (enterprise ? '' : '<span style="font-size:12px; color:' + color + '; font-weight:600; flex-shrink:0;">' + pct + '%</span>') +
    '</div>';

  // The bar: taller, rounded, subtle track. Enterprise shows a calm full-accent
  // sliver rather than alarming red at high %.
  const fillW = enterprise ? Math.min(100, pct) : pct;
  const bar =
    '<div style="height:10px; background: var(--bg-elevated); border-radius:999px; overflow:hidden;">' +
      '<div style="width:' + fillW + '%; height:100%; background:' + (enterprise ? 'var(--info)' : color) + '; border-radius:999px; transition: width .3s ease;"></div>' +
    '</div>';

  // Reset line + over-limit note as their own muted/strong rows.
  const resetLine = resetsIn
    ? '<div style="margin-top:6px; font-size:12px; color: var(--text-tertiary);">Resets in ' + resetsIn + '</div>'
    : '';
  const overMsg = c.over_limit
    ? (c.hard_block
        ? '<div style="margin-top:6px; font-size:12px; color: var(--danger); font-weight:600;">Daily limit reached &mdash; event pushes paused until reset. Upgrade for a higher limit.</div>'
        : '<div style="margin-top:6px; font-size:12px; color: var(--warning); font-weight:600;">Over the soft limit (no block on your plan).</div>')
    : '';

  el.innerHTML = header + bar + resetLine + overMsg;
}

// renderMachineCount appends a "Machines" line under the members/seats line on
// the billing page, using the coordinator's corrected machine signals. Numbers
// only (no user-controlled strings) → no XSS surface. Best-effort: a fetch
// failure leaves the members line untouched. `target` is the membersDesc element
// we append a second line into.
async function renderMachineCount(target, isPaid) {
  if (!target) return;
  const m = await api('/v1/me/machines?limit=1'); // counts only; cheap
  const running = m.running_count || 0;   // daemons alive (push OR pull, ~5min)
  const slots = m.active_count || 0;       // machines holding a concurrency slot
  const cap = m.active_cap || 0;           // tier's machine cap
  const n = (x) => (x || 0).toLocaleString();
  // Slots vs cap is the billing-relevant number (that's what's capped); show the
  // honest "running" alongside it. Warn when at/over the slot cap.
  // Comp accounts are uncapped (active_cap_unlimited) — render "unlimited"
  // instead of a false "you've used all the machines" warning.
  if (m.active_cap_unlimited) {
    target.insertAdjacentHTML('beforeend',
      '<div style="margin-top: 6px; color: var(--text-secondary);">' +
      n(slots) + ' machine' + (slots === 1 ? '' : 's') + ' in use &mdash; unlimited (complimentary)' +
      ' &middot; <span style="color: var(--text-tertiary);">' + n(running) + ' running right now</span></div>');
    return;
  }
  const atCap = cap > 0 && slots >= cap;
  const color = atCap ? 'var(--warning)' : 'var(--text-secondary)';
  let line = '<div style="margin-top: 6px; color: ' + color + ';">' +
    n(slots) + ' of ' + n(cap) + ' machine' + (cap === 1 ? '' : 's') + ' in use' +
    ' &middot; <span style="color: var(--text-tertiary);">' + n(running) + ' running right now</span>';
  if (atCap) {
    line += ' &mdash; <span style="font-weight: 600;">you\'ve used all the machines your plan covers</span>' +
      (isPaid ? '' : ' — upgrade to use more');
  }
  line += '</div>';
  // insertAdjacentHTML (NOT innerHTML +=): appending via innerHTML re-serializes
  // and re-parses the element, destroying JS-attached listeners on existing
  // children — which would silently kill the "Buy more seats" onclick wired onto
  // membersDesc just before this runs. insertAdjacentHTML inserts the new node
  // without touching the existing ones, preserving that listener.
  target.insertAdjacentHTML('beforeend', line);
}

export async function renderConsumptionMeter() {
  const el = $('billing-usage-desc');
  if (!el) return;
  // Public tier labels (DB ids solo/startup/enterprise → Free/Team/Enterprise).
  const labelFor = (tier) => tier === 'startup' ? 'Team' : tier === 'enterprise' ? 'Enterprise' : 'Free';

  let c;
  try {
    c = await api('/v1/billing/consumption');
  } catch (err) {
    // Don't leave a bare placeholder. Show the meter shell with whatever tier we
    // already know (from /v1/billing/status, cached on window._billing) + a muted
    // "couldn't refresh" note, so the row always reads as a usage meter.
    const tier = (window._billing && window._billing.tier) || 'solo';
    el.innerHTML =
      '<div style="display:flex; align-items:baseline; justify-content:space-between; margin-bottom:8px;">' +
        '<span style="color: var(--text);"><strong>' + labelFor(tier) + ' plan</strong></span>' +
      '</div>' +
      '<div style="height:10px; background: var(--bg-elevated); border-radius:999px;"></div>' +
      '<div style="margin-top:6px; font-size:12px; color: var(--text-tertiary);">Usage couldn&rsquo;t be loaded right now &mdash; it&rsquo;ll refresh shortly.</div>';
    return;
  }

  const tierLabel = labelFor(c.tier);
  const paint = () => paintUsageMeter(el, c, tierLabel);
  paint();

  // Tick the countdown once a minute (cheap, no network). Clear any prior timer.
  // stopConsumptionMeter() (called from the billing page's stop()) tears this
  // down so it doesn't run while the tab is hidden.
  if (_consumptionCountdownTimer) clearInterval(_consumptionCountdownTimer);
  _consumptionCountdownTimer = setInterval(paint, 60000);

  // Upgrade nudge at >=80% or over limit, deduped per reset window so we don't
  // re-prompt on every poll. Solo's exhaustion is the free->paid driver.
  if ((c.pct >= 80 || c.over_limit) && c.suggested_tier && c.suggested_tier !== c.tier) {
    const dismissKey = 'ac-usage-prompt-dismissed-' + (c.resets_at || '');
    if (!localStorage.getItem(dismissKey)) {
      maybeShowConsumptionUpgrade(c, dismissKey);
    }
  }
}

// stopConsumptionMeter — teardown for the countdown ticker. Called from the
// settings page's stop() hook so the interval doesn't run forever after the
// user leaves Settings (the section is hidden via .active toggle, never removed
// from the DOM, so the timer would otherwise keep painting an off-screen node).
export function stopConsumptionMeter() {
  if (_consumptionCountdownTimer) {
    clearInterval(_consumptionCountdownTimer);
    _consumptionCountdownTimer = null;
  }
}

// maybeShowConsumptionUpgrade — a lightweight, non-blocking nudge. Reuses the
// existing toast surface rather than a modal so it doesn't interrupt; marks the
// window dismissed so it shows at most once per 24h reset window.
function maybeShowConsumptionUpgrade(c, dismissKey) {
  const tierName = c.suggested_tier === 'enterprise' ? 'Enterprise' : 'Team';
  const verb = c.over_limit ? 'reached your daily event limit' : 'used ' + c.pct + '% of your daily events';
  showToast('info', 'You’ve ' + verb + '. Upgrade to ' + tierName + ' for a higher limit — Settings → Billing.');
  try { localStorage.setItem(dismissKey, '1'); } catch (_) { /* private mode */ }
}

export async function startCheckout(tier) {
  // Seat quantity = distinct human users on this team, NOT the number of
  // registered laptops. Use billing.member_count (authoritative count from
  // /v1/billing/status, which is COUNT(DISTINCT supabase_user_id) + pending
  // invites). Falling back to state.members.length counted laptops and
  // overcharged solo users with multiple machines.
  const memberCount = Math.max(1, (window._billing && window._billing.member_count) || 1);
  // Admins can pre-purchase a bucket of licenses by setting the seat
  // input higher than member_count — the use case is "buy 25 seats
  // for the team I'm onboarding next week." We clamp to [memberCount,
  // 1000]: never below the current count (Stripe would reject), never
  // above the server-side guard (also enforced at coordinator.ts).
  const qtyInput = document.getElementById('billing-seat-qty');
  let seats = memberCount;
  if (qtyInput) {
    const raw = parseInt(qtyInput.value, 10);
    if (Number.isFinite(raw) && raw > 0) {
      seats = Math.max(memberCount, Math.min(1000, raw));
    }
  }
  try {
    const data = await apiWithMessage('/v1/billing/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier, quantity: seats }),
    });
    if (data.url) {
      track('billing.checkout_started', { tier, quantity: seats });
      location.href = data.url;
    }
  } catch (e) {
    showToast('error', 'Failed to start checkout: ' + (e.message || 'unknown'));
  }
}

async function openBillingPortal() {
  try {
    const data = await apiWithMessage('/v1/billing/portal', { method: 'POST' });
    if (data.url) {
      track('billing.portal_opened');
      location.href = data.url;
    }
  } catch (e) {
    showToast('error', 'Failed to open billing portal: ' + (e.message || 'unknown'));
  }
}

// routeBillingChange decides between in-place plan change and a
// fresh Checkout session, based on authoritative billing state.
//
// The bug we're fixing: prior gate routed "active || trialing"
// → change-plan, anything else → checkout. But a user with a
// canceled or past_due paid sub falling into the else branch
// would hit Checkout, which the worker would then grant another
// 14-day trial for (the worker's hasHadTrial guard catches some
// cases via stripe_subscription_id history, but the dashboard's
// own routing should not even *attempt* a trial path for someone
// who has subscribed before).
//
// New rule: any tier other than 'solo' means "this account has
// engaged with billing before — never re-pitch a free trial."
// If change-plan fails server-side (e.g. the Stripe sub is gone
// entirely), surface the error rather than silently fall through
// to a trial-granting checkout.
export function routeBillingChange(tier) {
  const b = window._billing || {};
  const everPaid = b.tier && b.tier !== 'solo';
  if (everPaid) {
    changePlan(tier);
  } else {
    startCheckout(tier);
  }
}

async function changePlan(tier) {
  // Downgrade between paid tiers (currently only Enterprise → Startup) gets
  // a confirmation modal. Upgrades stay friction-free — the user can always
  // downgrade back if they change their mind, and the prorated charge is small.
  const billing = window._billing || {};
  const currentTier = billing.tier || '';
  const isDowngrade = (currentTier === 'enterprise' && tier === 'startup');
  if (isDowngrade) {
    const ok = await showBillingChangeConfirm({
      currentTier,
      targetTier: tier,
      periodEnd: billing.current_period_end,
      confirmLabel: 'Downgrade to Startup',
      danger: false,
    });
    if (!ok) return;
  }

  try {
    await apiWithMessage('/v1/billing/change-plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier }),
    });
    track('billing.plan_changed', { tier, was_downgrade: isDowngrade });
    // Collapse picker and refresh
    window._pickerOpen = false;
    const picker = $('billing-plan-picker');
    if (picker) picker.style.display = 'none';
    renderBillingStatus();
    const tierName = tier === 'enterprise' ? 'Enterprise' : tier === 'startup' ? 'Startup' : 'Solo';
    showToast('success', isDowngrade
      ? 'Downgraded to ' + tierName + '. Prorated credit will apply to your next invoice.'
      : 'Plan updated to ' + tierName + '.');
  } catch (e) {
    // no_subscription = our billing snapshot said "paid" but the sub is gone
    // (downgraded mid-session by webhook/cron). A fresh Checkout is the only
    // path forward — and it's safe to route there now: the server decides
    // trial eligibility, so a previously-paid user can't re-trial.
    if (e && e.code === 'no_subscription') {
      await startCheckout(tier);
      return;
    }
    showToast('error', 'Failed to change plan: ' + (e.message || 'unknown'));
  }
}

async function cancelSubscription() {
  // Two-step confirmation flow for cancellations:
  //   1. Confirmation modal showing exactly which plan → which plan (Solo)
  //      and on which date (period end). Prevents "oh no I clicked the
  //      wrong button" moments.
  //   2. Optional survey asking why. User can skip by clicking "Never mind"
  //      on the survey without picking a reason.
  //
  // Refuse to proceed without loaded billing data — the confirmation
  // message shows the user's current tier and period_end, and showing
  // the wrong tier (or assuming a default) on a destructive action could
  // trick them into cancelling a plan they didn't intend.
  const billing = window._billing;
  if (!billing || !billing.tier || billing.tier === 'solo') {
    showToast('error', 'Billing status isn’t loaded yet. Refresh Settings and try again.');
    return;
  }
  const confirmed = await showBillingChangeConfirm({
    currentTier: billing.tier,
    targetTier: 'solo',
    periodEnd: billing.current_period_end,
    confirmLabel: 'Cancel subscription',
    danger: true,
  });
  if (!confirmed) return;

  const reason = await showCancellationSurvey();
  if (reason === null) return; // User backed out at the survey.
  try {
    // Submit reason (best-effort — don't block cancellation on survey failure).
    if (reason && reason.code) {
      try {
        await apiWithMessage('/v1/billing/cancel-reason', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(reason),
        });
      } catch (_) { /* ignore — cancellation is the important part */ }
    }
    await apiWithMessage('/v1/billing/cancel', { method: 'POST' });
    track('billing.subscription_canceled', { reason_code: reason?.code || null });
    renderBillingStatus();
    // Success toast includes the end date so the user can screenshot it /
    // reference it later without opening the dashboard again.
    const endDate = billing.current_period_end
      ? new Date(billing.current_period_end).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
      : 'the end of the billing period';
    showToast('success', 'Subscription canceled. You’ll keep access until ' + endDate + ', then move to Solo.');
  } catch (e) {
    showToast('error', 'Failed to cancel subscription: ' + (e.message || 'unknown'));
  }
}

async function resumeSubscription() {
  try {
    await apiWithMessage('/v1/billing/resume', { method: 'POST' });
    track('billing.subscription_resumed');
    renderBillingStatus();
    showToast('success', 'Subscription resumed.');
  } catch (e) {
    showToast('error', 'Failed to resume subscription: ' + (e.message || 'unknown'));
  }
}

// ---- module init ----

function wireListeners() {
  // Wire billing buttons (event delegation since they render dynamically)
  document.addEventListener('click', (e) => {
    const id = e.target.id;
    if (id === 'billing-toggle-picker') {
      const picker = $('billing-plan-picker');
      if (picker) {
        window._pickerOpen = !window._pickerOpen;
        picker.style.display = window._pickerOpen ? '' : 'none';
        if (window._pickerOpen) picker.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      return;
    }
    if (id === 'billing-upgrade-startup') {
      routeBillingChange('startup');
      return;
    }
    if (id === 'billing-upgrade-enterprise') {
      routeBillingChange('enterprise');
      return;
    }
    if (id === 'billing-current-solo') {
      // Downgrade to Solo = cancel subscription at period end
      cancelSubscription();
      return;
    }
    if (id === 'billing-manage-portal') { openBillingPortal(); return; }
    if (id === 'billing-cancel-btn') { cancelSubscription(); return; }
    if (id === 'billing-resume-btn') { resumeSubscription(); return; }
  });

  // Handle return from Stripe — both initial checkout (?billing=success)
  // and portal returns (?billing=portal-return after a "buy more seats"
  // flow). Both refresh billing state and auto-resume any pending
  // invites that were saved before the redirect.
  //
  // Pre-fix only ?billing=success was handled — admins returning from
  // a "buy more seats" portal flow saw stale dashboard data AND their
  // ac-pending-invites payload sat in sessionStorage forever. Worst
  // case: a later legitimate checkout would fire stale invites
  // because the auto-resume code didn't realize the payload was old.
  (() => {
    const p = new URLSearchParams(location.search);
    const billingParam = p.get('billing');
    const isReturn = billingParam === 'success' || billingParam === 'portal-return';
    if (!isReturn) return;

    // Give the webhook a moment to land before re-rendering.
    // 2026-06-23: Billing is its own tab now (was the Account page).
    setTimeout(() => { if (state.activePage === 'billing') renderBillingStatus(); }, 1500);
    const clean = new URL(location.href);
    clean.searchParams.delete('billing');
    history.replaceState({}, '', clean.pathname + (clean.search === '?' ? '' : clean.search));
    track(billingParam === 'success' ? 'billing.checkout_completed' : 'billing.portal_returned');

    // If the user kicked off upgrade or buy-more-seats from the
    // "Out of seats" invite flow, send the saved invites now that
    // (presumably) seats are available. We try regardless of the
    // billing param because portal-return after a quantity update
    // also frees seats.
    try {
      const saved = sessionStorage.getItem('ac-pending-invites');
      if (!saved) return;
      sessionStorage.removeItem('ac-pending-invites');
      // Validate the payload shape AND the freshness — pre-fix a
      // stale payload from a never-completed flow would auto-fire
      // on the user's next unrelated checkout return.
      const parsed = JSON.parse(saved);
      const ts = parsed && parsed.savedAt;
      const STALE_MS = 30 * 60 * 1000; // 30 min cap
      if (!parsed || !Array.isArray(parsed.emails) || parsed.emails.length === 0 ||
          !ts || (Date.now() - ts) > STALE_MS) {
        return;
      }
      const { emails, role } = parsed;
      setTimeout(async () => {
        navigate('team');
        // Wait a beat for webhook to propagate the new state, then send
        setTimeout(async () => {
          try {
            await api('/v1/teams/invitations', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ emails, role }),
            });
            teamState.lastFetch = 0;
            renderTeamPage();
          } catch {}
        }, 2500);
      }, 500);
    } catch {}

    // PR G (2026-05-15): resume a pending Free-tier action across
    // the Stripe round-trip. PR D's upgrade-modal flow saves the
    // workspace/role the user was about to create when they hit
    // the 402; this consumes that saved intent on billing=success
    // and fires the originally-blocked create.
    //
    // Wrapped in its own try/catch + setTimeout so an error in
    // the resume doesn't break the invitations flow above.
    try {
      // PR G sweep finding #2 (HIGH): use peek + clear-on-success
      // so a failed auto-fire doesn't drop the user's intent. The
      // previous consume() pattern removed the storage entry before
      // the auto-fire ran, so if the webhook hadn't propagated yet
      // (2500ms isn't always enough) the user got "couldn't auto-
      // create" + the intent was gone forever. Now the intent stays
      // until either (a) the create succeeds + we clear it, or
      // (b) it goes stale (30min cap inside peekCheckoutResume).
      const resume = peekCheckoutResume();
      if (resume) {
        setTimeout(async () => {
          // Wait for webhook to propagate so the tier check
          // passes on the auto-fire. 2500ms matches the
          // invitations-resume cadence above; they share the
          // same propagation budget.
          await new Promise((r) => setTimeout(r, 2500));
          try {
            if (resume.kind === 'workspace') {
              await api('/v1/teams/bus', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: resume.payload.name }),
              });
              clearCheckoutResume();
              showToast('success', `Created "${resume.payload.name}" — upgrade complete`);
              // Navigate to Team (where the Workspaces panel lives
              // post-IA-PR-B'' 2026-05-27) so the user sees the newly-
              // created workspace immediately. The panel's own
              // MutationObserver fires on .active class change and
              // refetches.
              navigate('team');
            } else if (resume.kind === 'role') {
              await api('/v1/teams/roles', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: resume.payload.name, scopes: resume.payload.scopes }),
              });
              clearCheckoutResume();
              showToast('success', `Created role "${resume.payload.name}" — upgrade complete`);
              navigate('roles');
            } else {
              // Unknown kind in storage — clear so we don't loop.
              clearCheckoutResume();
            }
          } catch (e) {
            // PR G sweep finding #2: intent STAYS in storage. The
            // user gets a retry hint, and the next billing=success
            // OR the user's next manual click (which calls
            // saveCheckoutResume to overwrite, OR triggers the
            // upgrade flow which peeks it) recovers gracefully.
            // The 30-min freshness cap inside peekCheckoutResume
            // prevents the intent from sitting in storage forever.
            console.warn('[checkout-resume] auto-create failed (intent kept for retry):', e);
            const kindLabel = resume.kind === 'workspace' ? 'workspace' : 'role';
            showToast('error', `Upgrade complete, but couldn't auto-create the ${kindLabel}. Try again from Settings — your ${kindLabel} name is saved for 30 minutes.`);
          }
        }, 500);
      }
    } catch {
      // Swallow — checkout-resume is best-effort polish; never
      // worth blocking the billing=success flow on a sessionStorage
      // weirdness.
    }
  })();

  // Honor ?tier=startup or ?tier=enterprise from marketing/docs pricing CTAs.
  // After boot completes, if the user is on Solo and asked for a paid tier, jump them straight to Stripe checkout.
  // Stored on window so boot() can call it once billing status is known.
  window._applyTierIntent = async function applyTierIntent() {
    try {
      const params = new URLSearchParams(location.search);
      const wantedTier = params.get('tier');
      if (!wantedTier || !['startup', 'enterprise'].includes(wantedTier)) return;
      const billing = await api('/v1/billing/status').catch(() => null);
      if (!billing) return;
      // Clear the param so a reload doesn't re-trigger
      const clean = new URL(location.href);
      clean.searchParams.delete('tier');
      clean.searchParams.delete('signup');
      history.replaceState({}, '', clean.pathname + (clean.search === '?' ? '' : clean.search));
      if (billing.tier === wantedTier) return; // already on it
      // Admins on Solo → checkout; admins on another paid tier → change plan; non-admins → just show picker
      const teamInfo = await api('/v1/teams/info').catch(() => ({ your_role: 'member' }));
      // 2026-06-23: Billing is its own tab now (was the Account page).
      if (teamInfo.your_role !== 'admin') { navigate('billing'); return; }
      navigate('billing');
      if (billing.tier === 'solo') {
        startCheckout(wantedTier);
      } else {
        changePlan(wantedTier);
      }
    } catch (e) {
      // Rather than swallowing silently (which leaves the user wondering why
      // clicking "Start trial" on pricing did nothing), surface the failure
      // as a toast + log. They can retry manually from Settings.
      // eslint-disable-next-line no-console
      console.error('[applyTierIntent]', e);
      try { showToast('error', 'Couldn’t start that plan automatically. Go to the Billing tab to pick one.'); } catch (_) {}
      try { navigate('billing'); } catch (_) {}
    }
  };

  // window.renderBillingStatus: required by an inline onclick="..."
  // string in the My Machines pager nudge ("Upgrade for more slots").
  // Inline handlers resolve names from window, not the module namespace.
  window.renderBillingStatus = renderBillingStatus;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
